import { useMemo, useCallback } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { generatePath, useNavigate } from 'react-router-dom';

import ActivateCardModal from '#client/components/ActivateCardModal';
import MoveMoneyChoiceViewModal from '#client/components/MoveMoney/MoveMoneyChoiceViewModal';
import OpenDebitCardMailingV2 from '#client/components/OpenDebitCardMailingV2';
import SetPinModal from '#client/components/SetPinModal';
import { MODAL_NAMES } from '#client/constants/modals';
import {
  FUND_BANK_ACCOUNT,
  ACTIVATE_NEW_DEBIT_CARD,
  ADD_VIRTUAL_CARD_TO_WALLET,
  ADDED_VIRTUAL_CARD_TO_WALLET,
  STRIPE_ACCOUNT_LEARN_MORE,
  COMPLETED_NEXT_UP_CARD,
  STRIPE_ACCOUNT_GET_PAID,
  SETUP_DEBIT_CARD_PIN,
  ORDER_PHYSICAL_DEBIT_CARD,
  DISMISSED_ORDER_PHYSICAL_DEBIT_CARD,
  SETUP_PAYMENTS
} from '#client/constants/nextUpCards';
import { MFE_PATHS } from '#client/constants/paths';
import { BASE_URL } from '#client/constants/url';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import useModalContext from '#client/hooks/useModalContext';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { MARK_DONE_NEXTUP_CARD_MUTATION } from '#client/mutations';
import { CLICKED_FOR_MERCHANT_ACCOUNT_MUTATION } from '#client/mutations/merchant';

const bannerViewEventProps = {
  appPageName: 'active_banking_dashboard',
  bannerType: 'info'
};

const useMapCards = (nextUpCards) => {
  const captureException = useCaptureException();
  const { createCommonErrorToast } = useToastContext();
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { id: bankingAccountId } = useBankingAccounts();
  const { openModal } = useModalContext();
  const moneyInvoicePath = useDashboardPath(MFE_PATHS.INVOICING);
  const navigate = useNavigate();
  const { removeCard, removeCardWithAnimation } = useNextUpCardsContext();

  const [markDone, { loading: markDoneLoading }] = useMutation(MARK_DONE_NEXTUP_CARD_MUTATION, {
    noThrow: true
  });

  const [markClickedMerchantAccountCTA] = useMutation(CLICKED_FOR_MERCHANT_ACCOUNT_MUTATION, {
    noThrow: true
  });

  const trackButtonPressed = useClickstreamCallback('buttonPressed');
  const handleMarkClickedMerchantCTA = useCallback(async () => {
    const { error } = await markClickedMerchantAccountCTA({
      variables: {
        input: {
          businessUuid: businessEntityUuid
        }
      },
      skip: markDoneLoading
    });

    if (error) {
      captureException(new Error('Failed to mark clicked merchant account CTA'), {
        level: 'warning'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityUuid, createCommonErrorToast, markClickedMerchantAccountCTA, markDoneLoading]);

  const handleMarkDone = useCallback(
    async (type, mutationType) => {
      const { error } = await markDone({
        variables: {
          params: {
            identifier: {
              businessUuid: businessEntityUuid,
              unitAccountId
            },
            cardType: mutationType,
            bankingAccountId
          }
        },
        skip: markDoneLoading
      });

      if (error) {
        createCommonErrorToast({ error });
      } else {
        removeCardWithAnimation(type);
      }
    },
    [
      businessEntityUuid,
      createCommonErrorToast,
      markDone,
      markDoneLoading,
      removeCardWithAnimation,
      unitAccountId,
      bankingAccountId
    ]
  );

  const cardsInfo = useMemo(
    () => [
      {
        type: FUND_BANK_ACCOUNT,
        isVisible: true,
        title: 'Add funds to your account',
        description:
          'Make a bank transfer, deposit a check, or link your ZenBusiness Banking account to another app or platform.',
        image: '/s2/vector/product-add-zb-bank-account.svg',
        eventProps: {
          elementName: FUND_BANK_ACCOUNT,
          ...bannerViewEventProps
        },
        actionButton: {
          label: 'Add funds',
          onClick: () => {
            openModal({
              component: MoveMoneyChoiceViewModal,
              name: MODAL_NAMES.MOVE_MONEY_CHOICE_VIEW_MODAL
            });
            trackButtonPressed({
              elementName: 'Add_funds_onboarding_checklist',
              elementText: 'Add funds',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: ORDER_PHYSICAL_DEBIT_CARD,
        isVisible: true,
        title: 'Order your debit card',
        description: 'Let us know where to send it!',
        image: '/s2/vector/product-zb-debit.svg',
        eventProps: {
          elementName: ORDER_PHYSICAL_DEBIT_CARD,
          ...bannerViewEventProps
        },
        actionButton: {
          label: 'Get card',
          onClick: () => {
            openModal({
              component: OpenDebitCardMailingV2
            });
            trackButtonPressed({
              elementName: 'order_debit_card_from_banking_nextup',
              elementText: 'Get card',
              appPageName: 'active_banking_dashboard'
            });
          }
        },
        markdoneButton: {
          label: 'Not now',
          onClick: () => {
            handleMarkDone(ORDER_PHYSICAL_DEBIT_CARD, DISMISSED_ORDER_PHYSICAL_DEBIT_CARD);
            trackButtonPressed({
              elementName: 'not_now_on_orderdc_banking_nextup',
              elementText: 'Not now',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: ACTIVATE_NEW_DEBIT_CARD,
        isVisible: true,
        title: 'Activate your physical debit card',
        description: 'Get your card ready for business expenses.',
        image: '/s2/vector/online-payments.svg',
        eventProps: {
          elementName: ACTIVATE_NEW_DEBIT_CARD,
          ...bannerViewEventProps
        },
        actionButton: {
          label: 'Activate',
          onClick: () => {
            openModal({
              component: ActivateCardModal
            });
            trackButtonPressed({
              elementName: 'Activate_card_onboarding_checklist',
              elementText: 'Activate',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: SETUP_DEBIT_CARD_PIN,
        isVisible: true,
        title: 'Set your debit card PIN',
        description:
          'Ensure you can use your physical debit card at all locations by adding a PIN.',
        image: '/s2/vector/product-zb-debit.svg',
        eventProps: {
          elementName: SETUP_DEBIT_CARD_PIN,
          ...bannerViewEventProps
        },
        actionButton: {
          label: 'Set PIN',
          onClick: () => {
            openModal({
              component: SetPinModal
            });
            trackButtonPressed({
              elementName: 'setup_pin_onboarding_checklist',
              elementText: 'Set PIN',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: ADD_VIRTUAL_CARD_TO_WALLET,
        isVisible: true,
        title: 'Add virtual card to digital wallet',
        description: 'Be ready for business expenses, online and on the go.',
        image: '/s2/vector/product-zb-debit.svg',
        eventProps: {
          elementName: ADD_VIRTUAL_CARD_TO_WALLET,
          ...bannerViewEventProps
        },
        actionButton: {
          label: 'Add card',
          onClick: () => {
            openModal({
              name: MODAL_NAMES.ADD_CARD_WALLET_MODAL,
              isOpenCardModalAfterClose: false
            });
            trackButtonPressed({
              elementName: 'Add_card_onboarding_checklist',
              elementText: 'Add card',
              appPageName: 'active_banking_dashboard'
            });
          }
        },
        disabled: markDoneLoading,
        markdoneButton: {
          label: 'Mark done',
          onClick: () => {
            handleMarkDone(ADD_VIRTUAL_CARD_TO_WALLET, ADDED_VIRTUAL_CARD_TO_WALLET);
            trackButtonPressed({
              elementName: 'mark_done_Add_to_wallet',
              elementText: 'Mark done',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: STRIPE_ACCOUNT_LEARN_MORE,
        // Marked as always visible. However, the display of the card in the end-to-end flow
        // depends on the merchant_account entitlement, which previously relied on the LD flag check.
        isVisible: true,
        title: 'Add payment options to invoices',
        description:
          'Accept online payments by card or ACH with a payments account powered by Stripe.',
        image: '/s2/vector/stripe-nextup-todo.svg',
        eventProps: {
          elementName: SETUP_PAYMENTS,
          ...bannerViewEventProps,
          bannerType: 'promo'
        },
        actionButton: {
          label: 'Learn more',
          onClick: async () => {
            await handleMarkClickedMerchantCTA();
            window.location.href = generatePath(
              ':BASE_URL/ms/company/:businessEntityUuid/application/start?redirectTo=banking',
              { BASE_URL, businessEntityUuid }
            );
            trackButtonPressed({
              elementName: 'learn_more_stripe_from_banking',
              elementText: 'Learn more',
              appPageName: 'active_banking_dashboard'
            });
          }
        },
        markdoneButton: {
          label: 'Mark done',
          onClick: () => {
            handleMarkDone(STRIPE_ACCOUNT_LEARN_MORE, STRIPE_ACCOUNT_LEARN_MORE);
            trackButtonPressed({
              elementName: 'mark_done_stripe_nextup_banking',
              elementText: 'Mark done',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: STRIPE_ACCOUNT_GET_PAID,
        isVisible: true,
        title: 'Get paid online',
        description: 'Use your payments account to get paid directly into your bank account.',
        image: '/s2/vector/stripe-nextup-todo.svg',
        eventProps: {
          elementName: STRIPE_ACCOUNT_GET_PAID,
          ...bannerViewEventProps
        },
        actionButton: {
          label: 'Go to Invoices',
          onClick: () => {
            handleMarkDone(STRIPE_ACCOUNT_GET_PAID, STRIPE_ACCOUNT_GET_PAID);
            trackButtonPressed({
              elementName: 'Stripe_invoices_onboarding_checklist',
              elementText: 'Go to Invoices',
              appPageName: 'active_banking_dashboard'
            });
            navigate(moneyInvoicePath);
          }
        },
        markdoneButton: {
          label: 'Mark as done',
          onClick: () => {
            handleMarkDone(STRIPE_ACCOUNT_GET_PAID, STRIPE_ACCOUNT_GET_PAID);
            trackButtonPressed({
              elementName: 'stripe_later_onboarding_checklist',
              elementText: 'Mark as done',
              appPageName: 'active_banking_dashboard'
            });
          }
        }
      },
      {
        type: COMPLETED_NEXT_UP_CARD,
        isVisible: true,
        title: 'Great job!',
        description: "You don't have any outstanding tasks.",
        image: '/s2/vector/nextup-task-complete.svg',
        eventProps: {
          elementName: COMPLETED_NEXT_UP_CARD,
          ...bannerViewEventProps
        },
        markdoneButton: {
          label: 'Dismiss',
          onClick: () => {
            trackButtonPressed({
              elementName: 'dismiss_onboarding_cards',
              elementText: 'Dismiss',
              appPageName: 'active_banking_dashboard'
            });
            removeCard(COMPLETED_NEXT_UP_CARD);
          }
        }
      }
    ],
    [
      moneyInvoicePath,
      navigate,
      markDoneLoading,
      openModal,
      handleMarkDone,
      handleMarkClickedMerchantCTA,
      trackButtonPressed,
      businessEntityUuid,
      removeCard
    ]
  );

  const cardsInfoWithOrder = useMemo(
    () =>
      nextUpCards
        .map(({ type, ...otherProps }) => {
          const card = cardsInfo.find((item) => item.type === type && item.isVisible);

          if (card) {
            return {
              ...card,
              ...otherProps
            };
          }
          return null;
        })
        .filter(Boolean),
    [cardsInfo, nextUpCards]
  );

  return cardsInfoWithOrder;
};

export default useMapCards;
